#define PI 3.14159265359

uniform float uPixelRatio;
uniform float uTime;
uniform vec2 uResolution;
uniform float uWarpStrength;
uniform sampler2D uNoiseMap;
uniform sampler2D uGrainTexture;
uniform float uGrainStrength;
uniform float uFluteWidth;
uniform float uFluteStrength;
uniform float uToneMapExposure;
uniform vec3 uC1;
uniform vec3 uC2;
uniform vec3 uC3;
uniform vec3 uC4;
uniform vec3 uC5;
uniform vec3 uBackground;
uniform int uLightMode;
uniform int uAlgo;
uniform vec2 uMouse;
uniform int uMouseMode;
uniform float uMouseStrength;
uniform float uMouseRadius;
uniform float uMouseActive;
uniform vec2 uMouseVelocity;
uniform float uRepelSoftness;
varying vec2 vUv;
varying vec2 vUvA;

// Outward displacement pushing p away from m, radius-shaped falloff.
// uRepelSoftness (eps) softens the core: near the cursor the push magnitude
// falls to zero instead of the direction whipping/flipping, calming the motion.
vec2 repelCenter(vec2 p, vec2 m, float s, float r) {
    vec2 d = p - m;
    float dist = length(d);
    float f = 1.0 - smoothstep(0.0, r, dist);
    return d / max(dist + uRepelSoftness, 1e-3) * f * s;
}

/**
 * Generates a dynamic scene of colorful Gaussian blobs with a fluted glass distortion effect.
 * param uv: Use a fluted uv for the input to keep the fluted glass effect
 */
vec3 GaussianBlobs(vec2 uv, vec2 mouse, int mode, float strength, float radius, float mActive) {
    // Multi-color gradient scene — sampled via flutedUv for fluted glass effect
    float t = uTime * 0.6 + 3.5; // time with an offset for more interesting animation

    // Blob positions with slow organic animation
    vec2 p1 = vec2(-0.28 + sin(t * 0.7 + 0.5) * 0.15,  0.06 + cos(t * 0.5) * 0.12); // blue
    vec2 p2 = vec2(-0.06 + sin(t * 0.4 + 1.2) * 0.18,  0.16 + cos(t * 0.6) * 0.15); // magenta
    vec2 p3 = vec2( 0.07 + sin(t * 0.5 + 3.4) * 0.2,  0.00 + cos(t * 0.4) * 0.14); // red
    vec2 p4 = vec2( 0.22 + sin(t * 0.3 + 2.3) * 0.24, -0.10 + cos(t * 0.7) * 0.14); // orange
    vec2 p5 = vec2( 0.30 + sin(t * 0.6 + 1.1) * 0.18,  0.06 + cos(t * 0.4) * 0.13); // teal

    if (mode == 6) {                 // Pin blob: blob 3 follows the cursor
        p3 = mix(p3, mouse, mActive);
    } else if (mode == 7) {          // Repel centers: push every center away
        float sc = strength * mActive;
        p1 += repelCenter(p1, mouse, sc, radius);
        p2 += repelCenter(p2, mouse, sc, radius);
        p3 += repelCenter(p3, mouse, sc, radius);
        p4 += repelCenter(p4, mouse, sc, radius);
        p5 += repelCenter(p5, mouse, sc, radius);
    }

    vec3 c1 = uC1;
    vec3 c2 = uC2;
    vec3 c3 = uC3;
    vec3 c4 = uC4;
    vec3 c5 = uC5;

    vec2 warpNoise = texture2D(uNoiseMap, vUv).rg * 2.0 - 1.0;
    vec2 warpedUv = uv + warpNoise * uWarpStrength;
    float d1 = dot(warpedUv - p1, warpedUv - p1);
    float d2 = dot(warpedUv - p2, warpedUv - p2);
    float d3 = dot(warpedUv - p3, warpedUv - p3);
    float d4 = dot(warpedUv - p4, warpedUv - p4);
    float d5 = dot(warpedUv - p5, warpedUv - p5);

    // Per-blob weights (Gaussian falloff * amplitude)
    float w1 = exp(-d1 * 12.0) * 1.4;
    float w2 = exp(-d2 * 20.0) * 2.0;
    float w3 = exp(-d3 *  9.0) * 1.6;
    float w4 = exp(-d4 * 15.0) * 1.3;
    float w5 = exp(-d5 * 25.0) * 0.8;

    // Cursor blob (mode 8): a 6th blob at the cursor, colored uC1.
    float d6 = dot(warpedUv - mouse, warpedUv - mouse);
    float w6 = (mode == 8) ? exp(-d6 / max(radius * radius, 1e-4)) * strength * mActive * 2.0 : 0.0;
    vec3 c6 = uC1;

    // Light mode: composite by mixing the background toward the blob color,
    // so blobs read as saturated color over a pale bg instead of adding to white.
    if (uLightMode == 1) {
        vec3 acc = c1 * w1 + c2 * w2 + c3 * w3 + c4 * w4 + c5 * w5 + c6 * w6;
        float wsum = w1 + w2 + w3 + w4 + w5 + w6;
        vec3 blob = wsum > 1e-4 ? acc / wsum : uBackground;
        float a = clamp(wsum * uToneMapExposure, 0.0, 1.0);
        return mix(uBackground, blob, a);
    }

    // Dark mode: background + additive Gaussian blobs (HDR, tone-mapped later)
    vec3 color = uBackground;
    color += c1 * w1 + c2 * w2 + c3 * w3 + c4 * w4 + c5 * w5 + c6 * w6;
    return color;
}

vec2 rotate2d(vec2 v, float angle) {
    float s = sin(angle);
    float c = cos(angle);
    mat2 m = mat2(c, -s, s, c);
    return m * v;
}

/**
 * Generates a dynamic scene of colorful Gaussian ellipses with a fluted glass distortion effect.
 * param uv: Use a fluted uv for the input to keep the fluted glass effect
 */
vec3 GaussianEllipses(vec2 uv, vec2 mouse, int mode, float strength, float radius, float mActive) {
    float t = uTime * 0.6 + 3.5;

    vec2 p1 = vec2(-0.32 + sin(t * 0.5 + 1.8) * 0.20, -0.12 + cos(t * 0.8 + 0.3) * 0.16);
    vec2 p2 = vec2( 0.10 + sin(t * 0.6 + 2.5) * 0.14,  0.24 + cos(t * 0.3 + 1.7) * 0.18);
    vec2 p3 = vec2(-0.15 + sin(t * 0.9 + 0.7) * 0.22, -0.08 + cos(t * 0.5 + 2.9) * 0.11);
    vec2 p4 = vec2( 0.28 + sin(t * 0.4 + 3.1) * 0.17,  0.18 + cos(t * 0.6 + 0.9) * 0.20);
    vec2 p5 = vec2(-0.05 + sin(t * 0.7 + 4.2) * 0.13, -0.20 + cos(t * 0.9 + 1.5) * 0.15);

    if (mode == 6) {                 // Pin blob: blob 3 follows the cursor
        p3 = mix(p3, mouse, mActive);
    } else if (mode == 7) {          // Repel centers: push every center away
        float sc = strength * mActive;
        p1 += repelCenter(p1, mouse, sc, radius);
        p2 += repelCenter(p2, mouse, sc, radius);
        p3 += repelCenter(p3, mouse, sc, radius);
        p4 += repelCenter(p4, mouse, sc, radius);
        p5 += repelCenter(p5, mouse, sc, radius);
    }

    vec3 c1 = uC1;
    vec3 c2 = uC2;
    vec3 c3 = uC3;
    vec3 c4 = uC4;
    vec3 c5 = uC5;

    vec2 warpNoise = texture2D(uNoiseMap, vUv).rg * 2. - 1.;
    vec2 warpedUv = uv + vec2(warpNoise.r * uWarpStrength, warpNoise.g * uWarpStrength * 0.2);

    vec2 dv1 = warpedUv - p1;
    vec2 dv2 = warpedUv - p2;
    vec2 dv3 = warpedUv - p3;
    vec2 dv4 = warpedUv - p4;
    vec2 dv5 = warpedUv - p5;

    // Per-blob rotation angles (radians)
    float a1 =  0.3;
    float a2 = -1.1;
    float a3 =  0.8;
    float a4 = -0.5;
    float a5 =  1.4;

    vec2 r1 = rotate2d(dv1, a1);
    vec2 r2 = rotate2d(dv2, a2);
    vec2 r3 = rotate2d(dv3, a3);
    vec2 r4 = rotate2d(dv4, a4);
    vec2 r5 = rotate2d(dv5, a5);

    // Elliptical Gaussian distance: dx^2 * ax + dy^2 * ay
    // ax != ay stretches each blob into an ellipse; equivalent to exp(-dot(d,d)*w) when ax == ay == w
    float e1 = r1.x * r1.x *  8.0 + r1.y * r1.y * 1.0;
    float e2 = r2.x * r2.x * 25.0 + r2.y * r2.y * 12.0;
    float e3 = r3.x * r3.x *  6.0 + r3.y * r3.y * 14.0;
    float e4 = r4.x * r4.x * 20.0 + r4.y * r4.y *  8.0;
    float e5 = r5.x * r5.x * 30.0 + r5.y * r5.y * 15.0;

    float w1 = exp(-e1) * 1.4;
    float w2 = exp(-e2) * 2.0;
    float w3 = exp(-e3) * 1.6;
    float w4 = exp(-e4) * 1.3;
    float w5 = exp(-e5) * 0.8;

    // Cursor blob (mode 8): a 6th blob at the cursor, colored uC1.
    float d6 = dot(warpedUv - mouse, warpedUv - mouse);
    float w6 = (mode == 8) ? exp(-d6 / max(radius * radius, 1e-4)) * strength * mActive * 2.0 : 0.0;
    vec3 c6 = uC1;

    // Light mode: mix background toward blob color (see GaussianBlobs)
    if (uLightMode == 1) {
        vec3 acc = c1 * w1 + c2 * w2 + c3 * w3 + c4 * w4 + c5 * w5 + c6 * w6;
        float wsum = w1 + w2 + w3 + w4 + w5 + w6;
        vec3 blob = wsum > 1e-4 ? acc / wsum : uBackground;
        float a = clamp(wsum * uToneMapExposure, 0.0, 1.0);
        return mix(uBackground, blob, a);
    }

    vec3 color = uBackground;
    color += c1 * w1 + c2 * w2 + c3 * w3 + c4 * w4 + c5 * w5 + c6 * w6;
    return color;
}

void main() {
    vec2 mappedCoords = gl_FragCoord.xy / uPixelRatio - uResolution * 0.5;

    // Scale the whole composition (flute width, refraction, and blob field) with
    // the viewport width relative to a reference design width, so the pattern
    // reads the same proportionally on any screen instead of being pinned to
    // absolute pixels. At the reference width vpScale == 1 (identity).
    float vpScale = uResolution.x / 1440.0;

    // Set up fluted/reeded effect
    // The fluted coordinates is basically a transformation of the mapped coordinates to simulate the horizontal refraction of fluted glass.
    float fluteWidth = uFluteWidth * vpScale;
    float fluteStrength = uFluteStrength * vpScale;
    vec2 scaledUv = mappedCoords / vec2(fluteWidth);
    vec2 fractUv = vec2(fract(scaledUv.x), scaledUv.y);
    float flutedX = (fluteStrength) * (fractUv.x - 0.5);
    float flutedY = -(fluteStrength) * atanh(pow(fractUv.x, 6.));
    vec2 flutedCoords = vec2(mappedCoords.x + flutedX, mappedCoords.y + flutedY);
    vec2 flutedUv = flutedCoords / (1000. * vpScale);

    // Mouse interaction. Family A modes (1-5) transform the blob-sampling UV
    // here; Family B modes (6-8) leave sampleUv = flutedUv and act inside the
    // blob functions. All gated by uMouseActive (0 => identity => off).
    vec2 smoothUv = mappedCoords / (1000.0 * vpScale);
    vec2 toMouse = smoothUv - uMouse;
    float mouseDist = length(toMouse);
    float mouseFalloff = 1.0 - smoothstep(0.0, uMouseRadius, mouseDist);
    vec2 dir = toMouse / max(mouseDist, 1e-3);
    float amt = mouseFalloff * uMouseStrength * uMouseActive;

    // Note: Swirl/Lens rotate/scale flutedUv about uMouse, which lives in smooth
    // (non-fluted) space by design — mirroring Repel/Attract. The fixed point is
    // therefore refracted by up to the flute offset (sub-flute-width), reading as
    // glassy rather than perfectly cursor-centered. Intentional, not a bug.
    vec2 sampleUv = flutedUv;
    if (uMouseMode == 1) {          // Repel
        sampleUv = flutedUv + dir * amt;
    } else if (uMouseMode == 2) {   // Attract
        sampleUv = flutedUv - dir * amt;
    } else if (uMouseMode == 3) {   // Swirl
        sampleUv = uMouse + rotate2d(flutedUv - uMouse, amt * 3.14159265);
    } else if (uMouseMode == 4) {   // Drag: smear along cursor motion, clamped
        // so fast flicks at high strength don't sample far into empty field.
        vec2 drag = uMouseVelocity * amt * 15.0;
        float dmag = length(drag);
        drag = (dmag > 0.4) ? drag / dmag * 0.4 : drag;
        sampleUv = flutedUv - drag;
    } else if (uMouseMode == 5) {   // Lens (magnify)
        sampleUv = uMouse + (flutedUv - uMouse) * (1.0 - amt * 0.6);
    }

    vec3 color = (uAlgo == 0)
        ? GaussianBlobs(sampleUv, uMouse, uMouseMode, uMouseStrength, uMouseRadius, uMouseActive)
        : GaussianEllipses(sampleUv, uMouse, uMouseMode, uMouseStrength, uMouseRadius, uMouseActive);

    // Exponential tone mapping — compresses overbright, keeps rich colors.
    // Light mode already outputs LDR (bg-mixed), so only tone-map the additive dark path.
    if (uLightMode == 0) {
        color = 1.0 - exp(-color * uToneMapExposure);
    }

    // Film grain effect
    float grain = texture2D(uGrainTexture, vUvA).r * 2.0 - 1.0; // Convert from [0, 1] to [-1, 1]
    color += grain * uGrainStrength * max(color.r, max(color.g, color.b)); // Modulate grain strength by the brightness of the pixel
    color = clamp(color, 0.0, 1.0);

    gl_FragColor = vec4(color, 1.0);
}